import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { CustomerAuthApiModel, CustomerAuthApiResendModel, CustomerAuthListModel, CustomerAuthModel } from '../../models/customer.model';
import { ListMetadata } from '../../models/list-metadata.model';
import { HttpClientService } from '../httpClient/httpclient.service';
import { MessageService } from 'primeng/primeng';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class CustomersAdminService {

  public baseUrl = '/koncentro-customers/customer-auth';
  public baseAPIUrl = '/koncentro-api/customer';

  customerAuthStateMappings = [
    {
      id: 0,
      value: 'active'
    },
    {
      id: 1,
      value: 'deleted'
    },
    {
      id: 2,
      value: 'password_reset'
    },
    {
      id: 3,
      value: 'in_validation'
    },
    {
      id: 7,
      value: 'in_migration'
    }
  ];

  customerAuthCardStateMappings = [
    {
      id: 0,
      value: 'active'
    },
    {
      id: 1,
      value: 'deleted'
    },
    {
      id: 2,
      value: 'replaced'
    },
    {
      id: 7,
      value: 'unlinked'
    }
  ];

  constructor(
    private h: HttpClientService,
    private srvNotify: MessageService,
    private translate: TranslateService
  ) { }

  getFlags() {
    return this.customerAuthStateMappings;
  }

  getCardStates() {
    return this.customerAuthCardStateMappings;
  }

  getFlagKey(flg_state: number): string {
    return this.customerAuthStateMappings.find(m => m.id == flg_state).value;
  }

  getCardStateKey(customerCardState: number): string {
    return this.customerAuthCardStateMappings.find(m => m.id == customerCardState).value;
  }

  list(
    offset = null,
    limit = null,
    search?: Array<{ key: string, value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: Array<CustomerAuthListModel>, metadata: ListMetadata }> {
    const indexFlgCompany = search.findIndex(f => f.key === "flgCompany");
    const indexFlgState = search.findIndex(f => f.key === "flg_state");
    const indexAccountTypeDescription = search.findIndex(f => f.key === "account_type_description");
    const indexUserAccount = search.findIndex(f => f.key === "user_account");
    const indexHostId = search.findIndex(f => f.key === "host_id");
    const indexSurname = search.findIndex(f => f.key === "surname");
    const indexName = search.findIndex(f => f.key === "name");
    const indexTsIns = search.findIndex(f => f.key === "ts_ins");
    const indexCardState = search.findIndex(f => f.key === "customerCardState");
    const indexCardNo = search.findIndex(f => f.key === "card_no");
    const indexInsegna = search.findIndex(f => f.key === "insegna");
    //fix KS-6545 per lista carte
    if (indexFlgCompany == -1 && indexFlgState == -1
      && indexAccountTypeDescription == -1
      && indexUserAccount == -1
      && indexHostId == -1
      && indexSurname == -1
      && indexName == -1
      && indexCardState == -1
      && indexCardNo == -1
      && indexInsegna == -1
      && indexTsIns == -1) {
      this.srvNotify.add({
        severity: 'error', summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('card.addFilterTable')
      });
      return;
    }
    let params = HttpClientService.buildListParams(offset, limit, search, globalSearch, sort, sort_dir);

    // FIXME: il replace è stato necessario perché un bug di angular non fa l'escape del +
    // FIXME: valutare se gestire la cosa con un interceptor custom come indicato qui https://stackoverflow.com/questions/45428842/angular-url-plus-sign-converting-to-space
    return this.h.postPaged(this.baseUrl + '/getCustomerAuthPagedList' +
      (params.toString() ? '?' + params.toString().replace(/\+/gi, '%2B') : ''), {})
      .map(res => {
        res.data['ts_ins'] = new Date(res.data['ts_ins'])
        console.log(res)
        return res
      })
  }

  get(id: number): Observable<CustomerAuthModel> {
    return this.h.get(`${this.baseUrl}/${id}`)
  }

  getHistoryByCustomerAuth(customerAuthId: number) {
    return this.h.get(`${this.baseUrl}/${customerAuthId}/history`)
  }

  getTokensByCustomerAuth(customerAuthId: number) {
    return this.h.get(`${this.baseUrl}/${customerAuthId}/tokens`)
  }

  deleteCustomerAuth(customerAuthId: number) {
    return this.h.delete(`${this.baseUrl}/${customerAuthId}`)
  }

  generateSecurityCodeForPasswordReset(authData: CustomerAuthApiModel) {
    // { "userAccount": "newman@test.com", "accountType": "M", "sourceCode": "009", "companyCode": 52, "notifyUser": true }
    return this.h.post(`${this.baseAPIUrl}/generateSecurityCodeForPasswordReset`, authData)
  }

  resetPasswordWithSecurityCode(newData: any) {
    return this.h.post(`${this.baseAPIUrl}/resetPasswordWithSecurityCode`, newData)
  }

  resendCheckCode(authData: CustomerAuthApiResendModel) {
    return this.h.get(`${this.baseAPIUrl}/resendCheckCode`, {
      companyCode: authData.companyCode,
      sourceCode: authData.sourceCode,
      accountId: authData.accountId,
      accountType: authData.accountType
    })
  }

  recoverCustomerAuth(record) {
    return this.h.put(`${this.baseUrl}/update-login/${record.id}`, {
      login: record.user_account
    });
  }

  updateLogin(id: number, newLogin: string) {
    return this.h.put(`${this.baseUrl}/update-login/${id}`, {
      login: newLogin
    });
  }

  getExcelFilename(filters?: any[]) {
    let queryString = '';
    /* Se di tipo Array, filters corrisponde ai filtri della table e vengono inviati in query string,
       altrimenti ai filtri della ExtSearch, quindi vengono inviati come payload
    */
    let data = Array.isArray(filters) ? {} : filters;
    if (Array.isArray(filters) && filters.length > 0) {
      filters.forEach(filter => queryString += `${filter.key}=${filter.value}&`);
    }
    return this.h.post(queryString.length > 0 ? `${this.baseUrl}/exportCustomersAuth?${queryString}` : `${this.baseUrl}/exportCustomersAuth`, data);
  }

  sendExtSearchQuery(data): Observable<{ data: any, metadata: ListMetadata }> {
    if (data.sortField && data.sortDir) {
      return this.h.postPaged(`${this.baseUrl}/getCustomerAuthPagedList?offset=${data.offset}&limit=${data.limit}&sort=${data.sortField}&sort_dir=${data.sortDir}`, data.query);
    } else {
      return this.h.postPaged(`${this.baseUrl}/getCustomerAuthPagedList?offset=${data.offset}&limit=${data.limit}`, data.query);
    }
  }

  getCustomerAuthToken(customer) {
    return this.h.get(`${this.baseUrl}/getCustomerAuthToken?userName=${customer.user_account}&sourceCode=${customer.source_system_host_id}`);
  }

}



// WEBPACK FOOTER //
// ./src/app/services/customer/customers-admin.service.ts